import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, ArrowLeft, Edit, Trash2, Clock, Users, Filter, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BookingForm from '@/components/BookingForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReunioesAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    room: 'all',
    status: 'upcoming',
    sort_order: 'newest',
    sort_field: 'created_at'
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [bookings, filters]);

  const fetchData = async () => {
    try {
      const [bookingsRes, roomsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/rooms`)
      ]);
      setBookings(bookingsRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bookings];
    const now = new Date();

    // Filtro de status
    if (filters.status === 'upcoming') {
      filtered = filtered.filter(b => isAfter(parseISO(b.start_time), now));
    } else if (filters.status === 'past') {
      filtered = filtered.filter(b => isBefore(parseISO(b.end_time), now));
    } else if (filters.status === 'today') {
      const today = format(now, 'yyyy-MM-dd');
      filtered = filtered.filter(b => b.start_time.startsWith(today));
    }

    // Filtro de sala
    if (filters.room !== 'all') {
      filtered = filtered.filter(b => b.room_id === filters.room);
    }

    // Filtro de busca
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(b => 
        b.meeting_name.toLowerCase().includes(searchLower) ||
        b.responsible.toLowerCase().includes(searchLower)
      );
    }

    // Ordenar por data
    filtered.sort((a, b) => {
      const field = filters.sort_field === 'meeting_date' ? 'start_time' : 'created_at';
      const valueA = a[field] || a.start_time;
      const valueB = b[field] || b.start_time;

      if (filters.sort_order === 'oldest') {
        return new Date(valueA) - new Date(valueB);
      }

      return new Date(valueB) - new Date(valueA);
    });

    setFilteredBookings(filtered);
  };

  const handleEdit = (booking) => {
    setEditingBooking(booking);
    const room = rooms.find(r => r.id === booking.room_id);
    if (room) {
      setShowEditForm(true);
    }
  };

  const handleDelete = async (bookingId, hasRecurrence) => {
    let deleteSeries = false;
    
    if (hasRecurrence) {
      const choice = window.confirm(
        'Esta reunião faz parte de uma série recorrente.\n\n' +
        'Clique em OK para cancelar TODAS as reuniões da série\n' +
        'Clique em Cancelar para cancelar APENAS esta reunião'
      );
      deleteSeries = choice;
    } else {
      if (!window.confirm('Tem certeza que deseja cancelar esta reunião?')) {
        return;
      }
    }

    try {
      await axios.delete(`${API}/bookings/${bookingId}?delete_series=${deleteSeries}`);
      toast.success('Reunião cancelada com sucesso!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar reunião');
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Sala desconhecida';
  };

  const formatDateTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'HH:mm', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (booking) => {
    const now = new Date();
    const start = parseISO(booking.start_time);
    const end = parseISO(booking.end_time);

    if (isBefore(end, now)) {
      return <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">Finalizada</span>;
    } else if (isAfter(start, now)) {
      return <span className="px-3 py-1 rounded-full bg-secondary/20 text-secondary text-xs font-medium">Agendada</span>;
    } else {
      return <span className="px-3 py-1 rounded-full bg-destructive/20 text-destructive text-xs font-medium">Em andamento</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">Gerenciar Reuniões</h1>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-sm font-medium font-inter">{user?.name}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Stats & Filters */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-manrope font-bold tracking-tight mb-2">
                Todas as Reuniões
              </h2>
              <p className="text-muted-foreground font-inter">
                {filteredBookings.length} {filteredBookings.length === 1 ? 'reunião encontrada' : 'reuniões encontradas'}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-border/50 p-6 space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-primary" />
              <h3 className="font-manrope font-semibold">Filtros</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por nome ou responsável..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-3 h-10 border border-border rounded-md focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Room Filter */}
              <select
                value={filters.room}
                onChange={(e) => setFilters({ ...filters, room: e.target.value })}
                className="h-10 border border-border rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">Todas as salas</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>{room.name}</option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="h-10 border border-border rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="all">Todas</option>
                <option value="upcoming">Próximas</option>
                <option value="today">Hoje</option>
                <option value="past">Finalizadas</option>
              </select>

              <select
                value={filters.sort_order}
                onChange={(e) => setFilters({ ...filters, sort_order: e.target.value })}
                className="h-10 border border-border rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="newest">Mais novas primeiro</option>
                <option value="oldest">Mais antigas primeiro</option>
              </select>

              <select
                value={filters.sort_field}
                onChange={(e) => setFilters({ ...filters, sort_field: e.target.value })}
                className="h-10 border border-border rounded-md px-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="created_at">Ordenar por data de criação</option>
                <option value="meeting_date">Ordenar por data da reunião</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="text-center py-16 px-4 bg-white rounded-xl border border-border/50">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-manrope font-semibold mb-2">Nenhuma reunião encontrada</h3>
            <p className="text-muted-foreground font-inter">Tente ajustar os filtros</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => {
              const isPast = isBefore(parseISO(booking.end_time), new Date());
              
              return (
                <div
                  key={booking.id}
                  data-testid={`admin-booking-card-${booking.id}`}
                  className="bg-white rounded-xl border border-border/50 p-6 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                          <Calendar className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-xl font-manrope font-bold">
                              {booking.meeting_name}
                            </h4>
                            {getStatusBadge(booking)}
                            {booking.recurrence_type && (
                              <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                                Recorrente
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span>Responsável: <span className="font-medium text-foreground">{booking.responsible}</span></span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-secondary" />
                                <span className="font-medium text-foreground">{getRoomName(booking.room_id)}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              <span className="font-mono text-foreground">
                                {formatDateTime(booking.start_time)} - {formatTime(booking.end_time)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                Criada em <span className="font-mono text-foreground">{formatDateTime(booking.created_at)}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!isPast && (
                      <div className="flex gap-2">
                        <Button
                          data-testid={`admin-edit-booking-${booking.id}`}
                          onClick={() => handleEdit(booking)}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-primary hover:bg-primary hover:text-white"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          data-testid={`admin-delete-booking-${booking.id}`}
                          onClick={() => handleDelete(booking.id, booking.recurrence_id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-destructive hover:bg-destructive hover:text-white"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Form Modal */}
      {showEditForm && editingBooking && rooms.length > 0 && (
        <BookingForm
          room={rooms.find(r => r.id === editingBooking.room_id)}
          onClose={() => {
            setShowEditForm(false);
            setEditingBooking(null);
          }}
          onSuccess={() => {
            setShowEditForm(false);
            setEditingBooking(null);
            fetchData();
          }}
          existingBookings={bookings}
          isPublic={false}
          editingBooking={editingBooking}
        />
      )}
    </div>
  );
}
